import { useState, useEffect } from 'react'
import { RotateCcw, Download, Trash2, AlertTriangle, Sparkles } from 'lucide-react'
import { api } from '../../api/client'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../auth/AuthProvider'
import { NotificationsSection } from './NotificationsSection'

interface UserProfile {
  id: string
  email: string
  display_name: string
  timezone: string
}

// Per-rotina personalization (Tom / Verbosidade / Notas / Schedule) lives
// on /rotinas — each rotina's card expands with its own controls. Keeps
// Settings focused on profile + notifications + privacy.

const TIMEZONES = [
  'Europe/Lisbon', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Sao_Paulo', 'Asia/Tokyo', 'UTC',
]

export function SettingsPage() {
  const { user, signOut } = useAuth()
  const [, setProfile] = useState<UserProfile | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [timezone, setTimezone] = useState('Europe/Lisbon')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [reanalysing, setReanalysing] = useState(false)
  const [reanalysisMsg, setReanalysisMsg] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  const borderStyle = { borderColor: 'var(--border)' }
  const fieldClass = "w-full bg-white border rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"

  useEffect(() => {
    api.get<UserProfile>('/auth/me').then(data => {
      setProfile(data)
      setDisplayName(data.display_name)
      setTimezone(data.timezone)
    }).catch(() => {})
  }, [])

  async function saveSettings() {
    setSaving(true)
    try {
      await api.put('/auth/me', { display_name: displayName, timezone })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch { /* ignore */ }
    setSaving(false)
  }

  async function exportData() {
    setExporting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const baseUrl = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api'
      const response = await fetch(`${baseUrl}/user/export`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Extract filename from Content-Disposition if present
      const disposition = response.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename="([^"]+)"/)
      a.download = match ? match[1] : 'seguru-export.zip'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(`Erro ao exportar: ${err instanceof Error ? err.message : 'falha'}`)
    }
    setExporting(false)
  }

  async function reanalyse() {
    // Escape hatch for users who feel the system "doesn't know enough
    // about me" — kicks off the same pipeline that runs at first Gmail
    // connect (re-read calendar + email, regenerate the identity doc).
    // Backend dispatches to Celery; we show a one-line status and
    // poll lazily. If it's already running, the API tells us and we
    // just surface that.
    if (reanalysing) return
    setReanalysing(true)
    setReanalysisMsg(null)
    try {
      const res = await api.post<{ status: string }>('/onboarding/regenerate')
      setReanalysisMsg(
        res.status === 'already_running'
          ? 'Já estava a correr. Vai demorar alguns minutos.'
          : 'A correr. Os resultados aparecem no chat quando terminar (~1-2 min).',
      )
      setTimeout(() => setReanalysisMsg(null), 12000)
    } catch (err) {
      setReanalysisMsg(`Erro: ${err instanceof Error ? err.message : 'falha'}`)
      setTimeout(() => setReanalysisMsg(null), 8000)
    } finally {
      setReanalysing(false)
    }
  }

  async function deleteAccount() {
    if (deleteConfirmText !== 'APAGAR') return
    setDeleting(true)
    try {
      await api.delete('/user/account?confirm=DELETE')
      // Sign out and redirect to landing
      await signOut()
      window.location.href = '/'
    } catch (err) {
      alert(`Erro ao apagar: ${err instanceof Error ? err.message : 'falha'}`)
      setDeleting(false)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}>
      <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-6">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}>Definicoes</h1>

        {/* Profile */}
        <div className="bg-white rounded-lg border p-5" style={borderStyle}>
          <h2 className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: 'var(--ink-muted)' }}>Perfil</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--ink-muted)' }}>Email</label>
              <p className="text-sm" style={{ color: 'var(--ink-light)' }}>{user?.email}</p>
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--ink-muted)' }}>Nome</label>
              <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                className={fieldClass} style={{ ...borderStyle, color: 'var(--ink)' }} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--ink-muted)' }}>Fuso horario</label>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}
                className={fieldClass} style={{ ...borderStyle, color: 'var(--ink)' }}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <button onClick={saveSettings} disabled={saving}
              className="px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-40"
              style={{ background: 'var(--ink)', color: 'var(--cream)' }}
              onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent)' }}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--ink)')}>
              {saved ? 'Guardado' : saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>

        {/* Notificações (PWA push) */}
        <NotificationsSection />

        {/* Privacidade e dados */}
        <div className="bg-white rounded-lg border p-5" style={borderStyle}>
          <h2 className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: 'var(--ink-muted)' }}>Privacidade e dados</h2>
          <div className="space-y-3">
            <div>
              <button onClick={exportData} disabled={exporting}
                className="flex items-center gap-2 text-sm transition-colors border rounded-lg px-4 py-2 disabled:opacity-40"
                style={{ ...borderStyle, color: 'var(--ink-light)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-light)')}>
                {exporting ? <RotateCcw size={14} className="animate-spin" /> : <Download size={14} />}
                {exporting ? 'A preparar...' : 'Exportar os meus dados'}
              </button>
              <p className="text-xs mt-2" style={{ color: 'var(--ink-muted)' }}>
                Descarrega um ficheiro ZIP com toda a tua informação (memórias, conversas, vault, etc.) em formato legível. Cumpre o Artigo 20 do RGPD.
              </p>
            </div>

            {/* Re-analisar — escape hatch for "sistema não sabe o suficiente
                sobre mim". Re-scans Gmail + Calendar and regenerates the
                identity doc. Migrated from the removed /conhecimento page. */}
            <div className="pt-3 border-t" style={borderStyle}>
              <button
                onClick={reanalyse}
                disabled={reanalysing}
                className="flex items-center gap-2 text-sm transition-colors border rounded-lg px-4 py-2 disabled:opacity-40"
                style={{ ...borderStyle, color: 'var(--ink-light)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-light)')}
              >
                {reanalysing ? (
                  <RotateCcw size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {reanalysing ? 'A iniciar...' : 'Re-analisar Gmail e Calendário'}
              </button>
              <p className="text-xs mt-2" style={{ color: 'var(--ink-muted)' }}>
                Força uma re-leitura de 3 meses do teu Gmail + Calendar e reescreve o perfil interno. Útil se sentires que o seGuru já não te conhece bem.
              </p>
              {reanalysisMsg && (
                <p className="text-xs mt-2" style={{ color: reanalysisMsg.startsWith('Erro') ? '#dc2626' : 'var(--accent)' }}>
                  {reanalysisMsg}
                </p>
              )}
            </div>

            <div className="pt-3 border-t" style={borderStyle}>
              <button onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 text-sm transition-colors border rounded-lg px-4 py-2"
                style={{ borderColor: '#ef4444', color: '#ef4444' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2' }}
                onMouseLeave={e => { e.currentTarget.style.background = '' }}>
                <Trash2 size={14} />
                Apagar conta
              </button>
              <p className="text-xs mt-2" style={{ color: 'var(--ink-muted)' }}>
                Remove permanentemente a tua conta e todos os dados associados. Irreversível.
              </p>
            </div>
          </div>
        </div>

        {/* Account */}
        <div className="bg-white rounded-lg border p-5" style={borderStyle}>
          <h2 className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: 'var(--ink-muted)' }}>Conta</h2>
          <button onClick={signOut}
            className="text-sm transition-colors border rounded-lg px-4 py-2"
            style={{ ...borderStyle, color: 'var(--ink-light)' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-light)')}>
            Terminar sessao
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: '#fef2f2' }}>
                <AlertTriangle size={20} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <h3 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>Apagar conta permanentemente</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
                  Esta acção é <strong>irreversível</strong>. Serão apagados:
                </p>
                <ul className="text-sm mt-2 space-y-1 list-disc pl-5" style={{ color: 'var(--ink-muted)' }}>
                  <li>Todas as memórias e conversas</li>
                  <li>Todos os ficheiros do vault</li>
                  <li>Rotinas, briefings, perguntas</li>
                  <li>Ligação à tua conta Google</li>
                </ul>
                <p className="text-sm mt-3" style={{ color: 'var(--ink-muted)' }}>
                  Se queres guardar os teus dados, <strong>exporta primeiro</strong>.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                Para confirmar, escreve <strong>APAGAR</strong> abaixo:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="APAGAR"
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{ ...borderStyle, color: 'var(--ink)' }}
                autoFocus
              />
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText('') }}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-lg border disabled:opacity-40"
                style={{ ...borderStyle, color: 'var(--ink-light)' }}>
                Cancelar
              </button>
              <button
                onClick={deleteAccount}
                disabled={deleteConfirmText !== 'APAGAR' || deleting}
                className="px-4 py-2 text-sm rounded-lg disabled:opacity-40 flex items-center gap-2"
                style={{ background: '#ef4444', color: '#fff' }}>
                {deleting && <RotateCcw size={14} className="animate-spin" />}
                {deleting ? 'A apagar...' : 'Apagar conta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
